import { genId } from "@smartsheet/lodestar-utils";
import { EventHandler, SyntheticEvent, useMemo } from "react";
import { BaseOptionType } from "./types";

export const internalOptionToString = <Option extends BaseOptionType>(item: Option | null | undefined): string => {
    if (
        item === null ||
        item === undefined ||
        isCreatableMarkerOption(item) ||
        isSelectAllMarkerOption(item) ||
        isSelectV2OptionSeparator(item)
    ) {
        return "";
    } else if (Object.prototype.hasOwnProperty.call(item, "label")) {
        return item.label as string;
    } else {
        if (process.env.NODE_ENV !== "production") {
            console.warn(
                "The options provided to the Select or MultiSelect component do not have a label" +
                    " property. In this case, you must provide a the `optionToString` prop.",
                item
            );
        }
        return "";
    }
};

export const creatableMarkerOption = {
    value: "lodestar-select-createable-marker",
};

export const isCreatableMarkerOption = (option: unknown): option is typeof creatableMarkerOption =>
    option === creatableMarkerOption;

export const selectAllMarkerOption = {
    value: "lodestar-multiselect-select-all-marker",
};

export const isSelectAllMarkerOption = (option: unknown): option is typeof selectAllMarkerOption =>
    option === selectAllMarkerOption;

export const selectV2OptionSeparator = {
    value: "lodestar-select-separator-marker",
};

export const isSelectV2OptionSeparator = (option: unknown): option is typeof selectV2OptionSeparator =>
    option === selectV2OptionSeparator;

/**
 * We do this a lot in this component to make sure the container isn't handling
 * click events more than it should.
 */
export const stopPropagationHandler: EventHandler<SyntheticEvent> = (e) => {
    e.stopPropagation();
    // Don't want to send the click up to the ClickAwayListener! We have to use the big hammer here
    // since ClickAwayListener adds event handlers at the document level and we want to make sure
    // they never get there since this event handler and the ones from ClickAwayListener are within
    // the same bubbling phase.
    e.nativeEvent.stopImmediatePropagation();
};

export const useSelectIds = (type: "select" | "combobox", providedId: string | undefined) => {
    const idToUse = useMemo(() => providedId || genId("type"), [providedId]);
    const sharedIdProps = {
        id: idToUse,
        menuId: idToUse + "--menu",
        toggleButtonId: idToUse + "--toggle-button",
    };
    return type === "select"
        ? sharedIdProps
        : {
              ...sharedIdProps,
              inputId: idToUse,
          };
};

// squashes all consecutive separators when options between them get
// filtered out, and removes leading and trailing separators.
export const squashSeparators = <Option extends BaseOptionType>(options: Option[]) => {
    const squashedOptions = [];
    for (const [index, option] of options.entries()) {
        // escape early if we are adding a non-separator option
        if (!isSelectV2OptionSeparator(option)) {
            squashedOptions.push(option);
        } else {
            // only add non-consecutive separators
            const previousOption = options[index - 1];
            if (!isSelectV2OptionSeparator(previousOption) && !!previousOption) squashedOptions.push(option);
        }
    }
    // remove trailing separator after dealing with all consecutive options
    if (isSelectV2OptionSeparator(squashedOptions[squashedOptions.length - 1])) squashedOptions.pop();

    return squashedOptions;
};

/**
 * Used to find the next valid option after a given index
 *
 * isDescending is used to indicate the direction we are indexing towards,
 * eg. either towards the bottom of the list or towards the top
 */
export const findNextValidOptionIndex = <Option extends BaseOptionType>(
    options: Option[],
    startingIndex = 0,
    isDescending = true
) => {
    for (let idx = startingIndex; options[idx]; idx += isDescending ? 1 : -1) {
        if (!isSelectV2OptionSeparator(options[idx])) return idx;
    }
    return undefined;
};
